import React, { useCallback, useRef } from "react";
import { SelectPagination } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import { Subject } from "@models/ISchema";
import { SelectedSubject } from "@services/types/programming-structures/programmingStructuresFiltersTypes";
import { useSearchSubjects } from "@services/collections/subjects/subjectsServices";
import styles from "./subjectCodeSelector.module.scss";

interface SubjectOption {
  id: string;
  value: string;
  label: string;
  name: string;
}

interface SubjectCodeSelectorProps {
  scenarioId: string;
  originId: string;
  value: SelectedSubject | null;
  isDisabled?: boolean;
  onChange: (subject: SelectedSubject | null) => void;
}

const PAGE_SIZE = 20;
const DEBOUNCE_TIMEOUT = 500;

const toOption = (subject: SelectedSubject): SubjectOption => ({
  id: subject.code,
  value: subject.code,
  label: subject.code,
  name: subject.name,
});

const SubjectCodeSelector = ({
  scenarioId,
  originId,
  value,
  isDisabled,
  onChange,
}: SubjectCodeSelectorProps) => {
  const { t } = useTranslation();
  const { searchSubjects } = useSearchSubjects();
  const lastSearchTerm = useRef<string | null>(null);

  const loadOptions = useCallback(
    async (searchTerm: string, _loaded: unknown, additional?: { page: number }) => {
      const isSameSearch = lastSearchTerm.current === searchTerm;
      const page = isSameSearch ? (additional?.page ?? 1) : 1;

      lastSearchTerm.current = searchTerm;

      const { subjects, pageInfo } = await searchSubjects({
        scenarioId,
        originId,
        searchTerm,
        page,
        size: PAGE_SIZE,
      });

      return {
        options: (subjects ?? []).map((subject: Subject) => ({
          id: subject?.id,
          value: subject?.code ?? "",
          label: subject?.code ?? "",
          name: subject?.name ?? "",
        })),
        hasMore: Boolean(pageInfo?.hasNextPage),
        additional: { page: page + 1 },
      };
    },
    [searchSubjects, scenarioId, originId],
  );

  const formatOptionLabel = useCallback(
    (option: SubjectOption, meta: { context: "menu" | "value" }) => {
      if (meta?.context === "value") return option?.label;

      return (
        <div className={styles.subjectCodeSelector_option}>
          <span className={styles.subjectCodeSelector_code}>{option?.label}</span>
          {!!option?.name && <span className={styles.subjectCodeSelector_name}>{option.name}</span>}
        </div>
      );
    },
    [],
  );

  return (
    <SelectPagination
      className={styles.subjectCodeSelector}
      label={t("programming-structures.filters.subject-code.label")}
      placeholder={t("programming-structures.filters.subject-code.placeholder")}
      value={value ? toOption(value) : null}
      disabled={isDisabled}
      isClearable
      isDisabled={isDisabled}
      debounceTimeout={DEBOUNCE_TIMEOUT}
      additional={{ page: 1 }}
      loadOptions={loadOptions}
      formatOptionLabel={formatOptionLabel}
      onChange={(option: SubjectOption | null) =>
        onChange(option ? { code: option.value, name: option.name } : null)
      }
    />
  );
};

export default SubjectCodeSelector;
